import { Button, Heading, Input, InputOnChange, Modal, Text, useToast } from '@afterpaytouch/core'
import { ChangePasswordRequest, ErrorCode, isPortalApiError } from '@afterpaytouch/portal-api'
import { useTranslation } from 'next-i18next'
import React, { FunctionComponent, useEffect, useMemo, useState } from 'react'
import { useAmplitudeWithEnduringEventProperties } from '../../integrations/amplitude'
import { TrackingEvent } from '../../model/amplitude'
import { useChangePasswordMutation, useConsumerSelector, useLogout } from '../../state'
import { removeBiometricSettings } from '@afterpay/utils/biometric'
import { MaskedElement } from '@afterpaytouch/integrations/MaskedElement'
import { DDPrivacyLevel } from '@afterpaytouch/integrations/datadog'

const I18N_NAMESPACE = ['common', 'account']

interface EditPasswordModalProps {
  isModalOpen: boolean
  closeModal: () => void
}

type Fields<T> = {
  [P in keyof T]: { value: T[P]; hasUpdated: boolean; isError: boolean; error: string }
}

const initialFormState = {
  currentPassword: { value: '', hasUpdated: false, isError: false, error: '' },
  newPassword: { value: '', hasUpdated: false, isError: false, error: '' },
  confirmNewPassword: { value: '', hasUpdated: false, isError: false, error: '' },
}

export const EditPasswordModal: FunctionComponent<EditPasswordModalProps> = ({ isModalOpen, closeModal }) => {
  const { t } = useTranslation(I18N_NAMESPACE)
  const { email, phoneNumber } = useConsumerSelector()
  const setToast = useToast()
  const [form, setForm] = useState<Fields<ChangePasswordRequest['body']>>(initialFormState)
  const logout = useLogout()

  const MINIMUM_PASSWORD_STRENGTH = 2
  const [newPasswordStrength, setNewPasswordStrength] = useState<number | undefined>(undefined)
  const onPasswordStrengthChange = (strength: number): void => setNewPasswordStrength(strength)

  const { logEvent } = useAmplitudeWithEnduringEventProperties()

  const onShow = (): void => {
    logEvent(TrackingEvent.CLICKED_EDIT_PASSWORD)
  }

  const onUpdateForm = (e: InputOnChange): void => {
    setForm({ ...form, [e.target.name]: { ...form[e.target.name], value: e.target.value, hasUpdated: true } })
  }

  const [changePassword, { isLoading: isChangePasswordLoading }] = useChangePasswordMutation()

  const onChangePassword = async (): Promise<void> => {
    try {
      const res = await changePassword({
        body: { currentPassword: form.currentPassword.value, newPassword: form.newPassword.value, confirmNewPassword: form.confirmNewPassword.value },
      }).unwrap()
      if (res.status !== 'success') {
        throw new Error('invalid')
      }
      logEvent(TrackingEvent.EDIT_PASSWORD_SUCCESS)
      removeBiometricSettings(email, phoneNumber)
      handleCloseModal()
      setToast({ message: t('account:edit:password:toast:success'), kind: 'Success', manualDismiss: true, toastTime: 2000, testNameSpace })
    } catch (err) {
      logEvent(TrackingEvent.EDIT_PASSWORD_ERROR)
      if (isPortalApiError(err.data)) {
        if (err.data.errorCode === ErrorCode.Forbidden) {
          logout()
          return
        }

        if (err.data.errorCode === ErrorCode.PreconditionFailed) {
          setForm({ ...form, currentPassword: { ...form.currentPassword, isError: true, error: ErrorCode.PreconditionFailed } })
          return
        }
      }
      handleCloseModal()
      setToast({ message: t('account:edit:password:toast:error'), kind: 'Failure', manualDismiss: true, toastTime: 2000, testNameSpace })
    }
  }

  const isCurrentPasswordValid = useMemo(() => {
    return form.currentPassword.value.length > 0
  }, [form.currentPassword.value])

  const isNewPasswordUnique = useMemo(() => {
    return form.newPassword.value !== form.currentPassword.value
  }, [form.newPassword.value, form.currentPassword.value])

  const isNewPasswordValid = useMemo(() => {
    // @ts-ignore: OPERATION BLEED STOPPER
    return isNewPasswordUnique && newPasswordStrength >= MINIMUM_PASSWORD_STRENGTH
  }, [isNewPasswordUnique, newPasswordStrength])

  const isConfirmNewPasswordValid = useMemo(() => {
    return form.newPassword.value === form.confirmNewPassword.value
  }, [form.newPassword.value, form.confirmNewPassword.value])

  useEffect(() => {
    if (form.currentPassword.hasUpdated) {
      if (!isCurrentPasswordValid) {
        return setForm({ ...form, currentPassword: { ...form.currentPassword, isError: true } })
      }

      return setForm({ ...form, currentPassword: { ...form.currentPassword, isError: false } })
    }
  }, [form.currentPassword.value, isCurrentPasswordValid])

  useEffect(() => {
    if (form.newPassword.hasUpdated) {
      if (!isNewPasswordValid) {
        return setForm({ ...form, newPassword: { ...form.newPassword, isError: true } })
      }

      return setForm({ ...form, newPassword: { ...form.newPassword, isError: false } })
    }
  }, [form.newPassword.value, isNewPasswordValid])

  useEffect(() => {
    if (form.confirmNewPassword.hasUpdated) {
      if (!isConfirmNewPasswordValid) {
        return setForm({ ...form, confirmNewPassword: { ...form.confirmNewPassword, isError: true } })
      }

      return setForm({ ...form, confirmNewPassword: { ...form.confirmNewPassword, isError: false } })
    }
  }, [form.confirmNewPassword.value, isConfirmNewPasswordValid])

  const isFormSubmittable = isCurrentPasswordValid && isNewPasswordValid && isConfirmNewPasswordValid
  const isFormSubmitting = isChangePasswordLoading
  const isButtonDisabled = isFormSubmitting || !isFormSubmittable
  const isButtonLoading = isFormSubmitting
  const isInputDisabled = isFormSubmitting

  const testNameSpace = 'edit-password-modal'

  const resetForm = (): void => setForm(initialFormState)

  const handleCloseModal = (): void => {
    resetForm()
    closeModal()
  }

  return isModalOpen ? (
    <MaskedElement privacyLevel={DDPrivacyLevel.Hidden}>
      <Modal
        show={isModalOpen}
        onShow={onShow}
        onClose={handleCloseModal}
        onHide={handleCloseModal}
        onBackdropClick={handleCloseModal}
        onEscapeKeyDown={handleCloseModal}
        testNameSpace={testNameSpace}
      >
        <Modal.Header divider>
          <div className='text-center'>
            <Heading testNameSpace={testNameSpace} size='L'>
              {t('account:edit:password:header:heading')}
            </Heading>
          </div>
        </Modal.Header>
        <Modal.Content>
          <div>
            <div className='mt-2'>
              <Text>{t('account:edit:password:content:message')}</Text>
            </div>
            <div className='mt-4'>
              <Input.Password
                fullWidth
                required
                autoFocus
                testNameSpace={testNameSpace}
                disabled={isInputDisabled}
                error={form.currentPassword.isError}
                errorText={
                  form.currentPassword.error === ErrorCode.PreconditionFailed ? t('account:edit:password:content:currentPasswordError:precondition_failed') : ''
                }
                label={t('account:edit:password:content:currentPassword')}
                name='currentPassword'
                onChange={onUpdateForm}
              />
            </div>
            <div className='mt-4'>
              <Input.Password
                fullWidth
                required
                testNameSpace={testNameSpace}
                disabled={isInputDisabled}
                error={form.newPassword.isError}
                errorText={
                  isNewPasswordUnique ? t('account:edit:password:content:newPasswordError') : t('account:edit:password:content:newPasswordIsNotUniqueError')
                }
                label={t('account:edit:password:content:newPassword')}
                name='newPassword'
                onChange={onUpdateForm}
                onPasswordStrengthChange={onPasswordStrengthChange}
                passwordStrengthLabel={[
                  t('account:edit:password:content:passwordStrength:weak'),
                  t('account:edit:password:content:passwordStrength:good'),
                  t('account:edit:password:content:passwordStrength:great'),
                ]}
              />
            </div>
            <div className='mt-4'>
              <Input.Password
                fullWidth
                required
                testNameSpace={testNameSpace}
                disabled={isInputDisabled}
                error={form.confirmNewPassword.isError}
                errorText={t('account:edit:password:content:confirmNewPasswordError')}
                label={t('account:edit:password:content:confirmNewPassword')}
                name='confirmNewPassword'
                onChange={onUpdateForm}
              />
            </div>
            <div className='mt-4'>
              <Button padding='Fluid' loading={isButtonLoading} disabled={isButtonDisabled} onClick={onChangePassword}>
                <Text bold>{t('account:edit:password:content:cta')}</Text>
              </Button>
            </div>
          </div>
        </Modal.Content>
      </Modal>
    </MaskedElement>
  ) : null
}
